import { useEffect, useState, FC } from 'react';

import { ControlContainer } from '../../../components/controls';
import { NestedNavPages } from '../../../config/CoreConstants';
import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { headerActions } from '../../../store/slices/header/slice';
import { nickNameInfoSlice } from '../../../store/slices/nickname/selectors';
import { useIsLargerThanWide } from '../../../styles/hooks/media';
import { Icon, Icons } from '../../icons';
import { L12nText } from '../../text/L12nText';

import { NickNameEditFormConnected } from './NickNameEditFormConnected';
import { NickNameEditFooterMessageConnected } from './NickNameFooterMessageConnected';

import styles from './NickNameSettings.module.css';

interface NickNameSettingsConnectedProps {
  isNickNameInfoFetched: boolean;
}

export const NickNameSettingsConnected: FC<NickNameSettingsConnectedProps> = ({ isNickNameInfoFetched }) => {
  const [isNickNameEditing, setIsNickNameEditing] = useState(false);
  const { remainingAttempts, showNickNameSection } = nickNameInfoSlice.getNickNameInfo();
  const playerName = useConfigSlice.playerConfig()?.screenName;
  const isPencilButtonDisplayed = isNickNameInfoFetched && !!remainingAttempts;

  const dispatch = useCoreDispatch();
  const isWide = useIsLargerThanWide();
  const audioTrigger = useAudioTrigger();

  const handleEditNickName = (): void => {
    audioTrigger.play({ name: 'menuClick' });
    if (!isWide) {
      dispatch(headerActions.pushNavRoute(NestedNavPages.SETTINGS_NICKNAME_CHANGE));
    } else {
      setIsNickNameEditing(true);
    }
  };

  const onNickNameSave = (): void => {
    setIsNickNameEditing(false);
  };

  // Get out from form editing on Esc key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsNickNameEditing(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      {!isNickNameEditing ? (
        <ControlContainer
          label={<L12nText tk="SETTINGS_NICKNAME" />}
          description={<div className={styles.nickName_settings_description}>{playerName}</div>}
          offset
        >
          {isPencilButtonDisplayed && (
            <div className={styles.nickName_settings_edit_button} onClick={handleEditNickName}>
              <Icon icon={Icons.Edit} />
            </div>
          )}
        </ControlContainer>
      ) : (
        <div className={styles.nickName_settings_edit_wrapper}>
          <div className={styles.nickName_settings_label}>{<L12nText tk="SETTINGS_NICKNAME" />}</div>
          <div className={styles.nickName_settings_form}>
            <NickNameEditFormConnected nickName={playerName ?? ''} onNickNameSave={onNickNameSave} />
          </div>
        </div>
      )}
      {isNickNameInfoFetched && showNickNameSection ? <NickNameEditFooterMessageConnected /> : null}
    </>
  );
};
